import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { useSiteSettings } from '../context/SiteSettingsContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSiteSettings();
  const contactInfo = settings?.contact_info || {};
  const branding = settings?.branding || {};
  
  // Logo from public folder
  const logoUrl = "/sentora-logo.png";
  const companyName = branding.company_name || "Sentora Holidays";
  const tagline = branding.tagline || "a Unit of Sam Tours and Travels Pvt Ltd";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Packages', path: '/packages' },
    { name: 'Day Tours', path: '/day-tours' },
    { name: 'Fleet', path: '/fleet' },
    { name: 'AI Planner', path: '/ai-planner' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
        isScrolled 
          ? 'bg-s-bg/95 backdrop-blur-md shadow-lg' 
          : 'bg-gradient-to-b from-s-bg to-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <img src={logoUrl} alt="Sentora" className="h-14 md:h-16 w-auto object-contain" />
              <div className="flex flex-col">
                <span className="text-lg md:text-xl font-bold text-[#59090c] tracking-tight font-heritage leading-tight">
                  Sentora Holidays
                </span>
                <span className="text-[7px] md:text-[9px] text-s-text2 leading-tight tracking-wide">
                  a Unit of Sam Tours and Travels Pvt Ltd
                </span>
              </div>
            </Link>

            <div className="hidden lg:flex items-center gap-1 flex-shrink-0">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium tracking-wide transition-all duration-200 rounded-lg ${
                    location.pathname === link.path
                      ? 'text-s-text bg-[#59090c]/10'
                      : 'text-s-text2 hover:text-s-text hover:bg-[#59090c]/10'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-4">

              <Link to="/contact">
                <Button className="bg-[#59090c] hover:bg-[#59090c]/80 text-white rounded-full">
                  Get Quote
                </Button>
              </Link>
            </div>

            <div className="flex lg:hidden items-center gap-3">
              <button
                className="p-2 text-s-text relative z-[10000]"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Rendered outside nav for proper z-index stacking */}
      <div 
        className={`lg:hidden fixed inset-0 z-[9998] transition-all duration-300 ${
          isMobileMenuOpen ? 'visible' : 'invisible pointer-events-none'
        }`}
        data-testid="mobile-menu-overlay"
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div 
          className={`absolute top-16 md:top-20 left-0 right-0 bottom-0 bg-s-bg transition-transform duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
          data-testid="mobile-menu-panel"
        >
          <nav className="px-4 py-6 h-full overflow-y-auto" aria-label="Mobile navigation">
            <ul className="space-y-1">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className={`block px-4 py-4 rounded-xl text-lg font-medium transition-colors ${
                      location.pathname === link.path
                        ? 'text-s-text bg-s-card'
                        : 'text-s-text2 hover:text-s-text hover:bg-s-card/50'
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-8 space-y-3">
              <a 
                href={`tel:${contactInfo.phone}`}
                className="flex items-center justify-center gap-2 bg-s-card px-4 py-4 rounded-xl text-s-text font-medium"
              >
                <Phone size={20} />
                {contactInfo.phone}
              </a>
              <Link to="/contact">
                <Button className="w-full h-14 text-lg bg-gradient-to-r from-[#59090c] to-[#59090c] rounded-xl">
                  Get Free Quote
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Navbar;
